// eslint-disable-next-line unused-imports/no-unused-vars
namespace MsftSme {
    'use strict';
    export interface MsftSmeInit {
        entryPointType?: string;
        entryPointName?: string;
        mode: number;
        moduleName?: string;
        moduleVersion?: string;
        shellOrigin?: string;
        isProduction?: boolean;
        sessionId?: string;
        logLevel?: number;
        powerShellModuleName?: string;
        websocket?: boolean;
        sshWebsocket?: boolean;
        powerShellEndpoint?: string;
        powerShellPrefix?: string;
        sessionExpiration: number;
        performanceProfile: boolean;
        experiments?: string[];
        developerGuide: boolean;
        connectivityLevel: string;
        isShell: boolean;
        shellVersion: string;
        gatewayApiVersion: string;
        gatewayPlatform: string;
    }

    export interface MsftSmeEnvironment {
        name: string;
        displayName: string;
        origin: string;
        signature: string;
        shell: {
            name: string;
            origin: string;
        };
        resources: any[];
        entryPoints: any[];
        modules: any[];
        configuration: EnvironmentConfiguration;
        version: string;
        loadPath?: string;
        gatewayApiVersion: string;
        gatewayPlatform: string;
    }

    export interface MsftSmeAssets {
        css: string[];
        cssV2: string[];
        js: string[];
    }

    export interface MsftSmeExternalLibraries {
        // mapping of supported library versions to there library locations.
        // Caution. New library versions will almost always have a minimal
        // time where older versions must still be supported. (generally 2 GA releases)
        monaco: {
            '0.17.1': string;
            '0.36.1': string;
        };
        jsll: {
            '4.2.18': string;
        };
    }

    /**
     * Defines a mapping of an assets versions to its relative module paths
     */
    export interface MsftSmeVersionedAsset extends MsftSme.StringMap<string> { }

    /**
     * Defines a mapping of an asset name to its versioned paths
     */
    export interface MsftSmeModuleAssets extends MsftSme.StringMap<MsftSmeVersionedAsset> { }

    /**
     * Defines a mapping of an modules names to its shared asset collection
     */
    export interface MsftSmeSharedAssets extends MsftSme.StringMap<MsftSmeModuleAssets> { }

    export interface MsftSmeResources {
        localeId: string;
        localeRegionalId: string;
        strings: any;
        theme: string;
        assets: MsftSmeAssets;
        moduleAssets: MsftSmeSharedAssets;
        accessibilityMode: boolean;
        lib: MsftSmeExternalLibraries;
    }

    export interface EnvironmentConfiguration {
        gateway?: {
            disabled?: boolean
        };
        signOn?: {
            signedHttpRequestToken?: {
                jwt: string,
                username: string,
            }
        };
        application?: {
            theme?: 'light' | 'dark';
            locale?: string;
        };
        nodes?: {
            defaultCredentials?: {
                username: string,
                password: string
                encryptedWith?: 'jwk' | string
            }
        };
        notifications?: {
            redirectToHostWindow?: boolean
        };
        tools?: {
            hide?: boolean
        };
        connections?: {
            type?: 'standard' | 'server' | 'cluster',
            name?: string,
            nodeFqdns?: string[],
            header?: {
                hide?: boolean
            }
        };
        telemetry?: {
            sourceLibraryCdnLink?: string,
            sourceLibraryCdnIntegrityHash?: string
        };
        extensionsAutoUpdate?: {
            enable?: boolean,
            showAlert?: boolean
        };
        gatewayAutoUpdate?: {
            enable?: boolean
        };
        whatsNew?: {
            showDialog?: boolean
        };
        appBar?: {
            hide?: boolean
            title?: {
                hide?: boolean,
                value?: string,
                disableNavigation?: boolean
            },
            solution?: {
                default?: string,
                hide: boolean,
                hideDropdown: boolean
            },
            navigation?: {
                allowHistory: boolean,
                allowRefresh: boolean,
                allowUrlInput: boolean,
                allowHome: boolean
            },
            urlField?: {
                hideSolutionUrl: boolean
            },
            rightMenu?: {
                showScripts: boolean,
                showSettings: boolean,
                showAbout: boolean,
                showWindowControls: boolean,
                showShareLink: boolean
            },
            aboutDialog?: {
                showFeedback: boolean,
                feedbackLink: string,
                showDocumentation: boolean,
                documentationLink: string,
                releaseNotesLink: string,
                seeMoreReleaseDetailsLink: string,
                showLicense: boolean,
                showScriptsUseRights: boolean,
                showLicenseExtensions: boolean,
                showPrivacyStatement: boolean,
                privacyLink: string,
                showSupportPolicy: boolean,
                supportLink: string,
                showThirdPartyNotice: boolean,
                showShortcuts: boolean,
                openLinkWithoutHref: boolean,
                showAccessibilityCompliance: boolean,
                accessibilityComplianceLink: string,
                extensionLicenseLink: string,
                eulaLicenseLink: string
            },
            overview?: {
                gatewayStatusUpdateLink: string,
                updateLearnMoreLink: string
            }
        };
        settings?: {
            account?: {
                show: boolean
            },
            personalization?: {
                show: boolean
            },
            diagnostic?: {
                show: boolean
            },
            language?: {
                show: boolean
            },
            notification?: {
                show: boolean
            },
            extension?: {
                show: boolean
            },
            azure?: {
                show: boolean
            },
            access?: {
                show: boolean
            },
            sharedConnections?: {
                show: boolean
            },
            connectivity?: {
                show: boolean
            },
            general?: {
                show: boolean
            };
            updates?: {
                show: boolean
            }
            extensionUpdates?: {
                show: boolean
            }
            performance?: {
                show: boolean
            },
            advanced?: {
                show: boolean
            },
            proxy?: {
                show: boolean
            }
        };
    }

    /**
     * Electron Renderer
     * @see http://electron.atom.io/docs/api/ipc-renderer
     */
    export interface MsftSmeElectronIpcRenderer {
        on(channel: string, listener: Function): this;
        once(channel: string, listener: Function): this;
        removeAllListeners(channel: string): this;
        removeListener(channel: string, listener: Function): this;
        send(channel: string, ...args: any[]): void;
        sendSync(channel: string, ...args: any[]): any;
        sendTo(webContentsId: number, channel: string, ...args: any[]): void;
        sendToHost(channel: string, ...args: any[]): void;
    }

    /**
     * Electron Remote
     * @see http://electron.atom.io/docs/api/remote
     */
    export interface MsftSmeElectronRemote {
        getCurrentWindow(): {
            close(): void,
            minimize(): void,
            maximize(): void,
            unmaximize(): void,
            isMaximized(): boolean,
        };
    }

    export interface MsftSmeElectron {
        ipcRenderer: MsftSmeElectronIpcRenderer;
        remote: MsftSmeElectronRemote;
    }

    export interface MsftSmeHost {
        isElectron: boolean;
        isWeb: boolean;
        electron?: MsftSmeElectron;
    }

    export interface MsftSmeWindowContext {
        Init: MsftSmeInit;
        Environment: MsftSmeEnvironment;
        Resources: MsftSmeResources;
        Host: MsftSmeHost;
    }

    export interface SMEWindow extends Window {
        MsftSme: MsftSmeWindowContext;
    }

    export interface MessageEventListener extends EventListener {
        (evt: MessageEvent): void;
    }

    // The following focus is available on HTMLElement, however, focus is available on Element
    export interface Element {
        focus(): void;
    }

    export interface StringMap<T> {
        [key: string]: T;
    }

    export interface NumberMap<T> {
        [key: number]: T;
        length?: number;
    }

    export interface NameValue<N, T> {
        name: N;
        value: T;
    }

    export type Action = () => void;

    export type Action1<T> = (arg: T) => void;

    export type Action2<T1, T2> = (arg1: T1, arg2: T2) => void;

    export type Action3<T1, T2, T3> = (arg1: T1, arg2: T2, arg3: T3) => void;

    export type Func<R> = () => R;

    export type Func1<T, R> = (arg: T) => R;

    export type Func2<T1, T2, R> = (arg1: T1, arg2: T2) => R;

    export type Primitive = number | string | Date | boolean;

    export interface StringMapPrimitive extends StringMap<StringMapRecursive | Primitive | Array<Primitive | StringMapRecursive>> { }

    export type StringMapRecursive = StringMapPrimitive;

    export interface Obsolete { }

    ////////////////////////////////////////
    /// BEGIN: IE11 ECMAScript Extensions
    ////////////////////////////////////////

    export interface CollatorOptions extends Intl.CollatorOptions {
    }

    export interface Map<K, V> {
        delete(key: K): boolean;
        get(key: K): V;
        has(key: K): boolean;
        set(key: K, value: V): void;
        clear(): void;
        forEach(callbackfn: (value: V, index: K, map: Map<K, V>) => void, thisArg?: any): void;
    }

    ////////////////////////////////////////
    /// END: IE11 ECMAScript Extensions
    ////////////////////////////////////////

    export interface RandomSource {
        getRandomValues(array: ArrayBufferView): ArrayBufferView;
    }
}
